import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LazyWidgetLoaderService } from '../../../../plugins/services/lazy-widget-loader.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { Company } from '../../../../shared/interfaces/company';
import { DatePipe } from '@angular/common';
import { RecordNotesComponent } from '../../../../shared/components/record-notes/record-notes.component';

@Component({
  selector: 'app-assigned-lead-row',
  standalone: true,
  imports: [FormsModule, DatePipe, RecordNotesComponent],
  templateUrl: './assigned-lead-row.component.html',
  styleUrl: './assigned-lead-row.component.scss',
})
export class AssignedLeadRowComponent
  extends DestroyBase
  implements AfterViewInit
{
  private readonly areaCode = 'assigned-leads-table';
  @Input() rowId = 0;
  @Input({ required: true }) assignedLead!: Company;
  @Input({ required: true }) recordUniqueId!: string;
  @Input() checked = false;
  @Input() assignedToMe = false;
  showDropDown = false;
  showNotesPopup = false;

  lazyWidgetLoaderService = inject(LazyWidgetLoaderService);

  @Output() leadCheckedEmitter = new EventEmitter<{
    lead: Company;
    checked: boolean;
  }>();
  @Output() showDetailViewEmitter = new EventEmitter<Company>();
  @Output() unassignLeadEmitter = new EventEmitter<Company>();

  @ViewChild('leadAttributesContainer', { read: ViewContainerRef })
  leadAttributesContainer!: ViewContainerRef;

  get sourceBadgeLabel(): string {
    const source = (this.assignedLead?.source || '').toUpperCase();

    switch (source) {
      case 'PUBLIC':
        return 'DEEPROOTS';
      case 'CUSTOM':
        return 'REFERRAL';
      case 'CRF':
      case 'WNCSBI_APPS':
        return 'WNCSBI';
      case 'DATA_AXLE':
        return 'DATA AXLE';
      default:
        return source || 'UNKNOWN';
    }
  }

  get leadStatusLabel(): string {
    const status = this.assignedLead?.lead_status;
    if (!status) return '';

    // Map status values to display labels
    const statusMap: Record<string, string> = {
      '0': 'Not Set',
      '1': 'Under Review',
      '2': 'Further Info Required',
      '3': 'Prospective Client',
      '4': 'On Hold',
      '5': 'Declined',
      '6': 'Not Currently Pursuing',
      '7': 'Active Client',
      '8': 'Request Lead Contact',
      '9': 'Active Funded',
      '10': 'Active Funded Grant',
      '11': 'Active Technical Assistance',
    };

    return statusMap[status] || status;
  }

  async ngAfterViewInit(): Promise<void> {
    await this.lazyWidgetLoaderService.loadWidget(
      this.areaCode,
      this.leadAttributesContainer,
      { leadId: this.assignedLead.leadId },
    );
  }

  onCheckboxTicked() {
    if (!this.assignedLead.is_active) {
      return;
    }
    this.leadCheckedEmitter.emit({
      checked: this.checked,
      lead: this.assignedLead,
    });
  }

  showDetailView() {
    if (!this.assignedLead.is_active) {
      return;
    }
    this.showDetailViewEmitter.emit(this.assignedLead);
  }

  onOptionsClick() {
    this.showDropDown = !this.showDropDown;
  }

  closeDropDown() {
    this.showDropDown = false;
  }

  onUnassignLead() {
    if (!this.assignedLead.is_active) {
      return;
    }
    this.closeDropDown();

    this.unassignLeadEmitter.emit(this.assignedLead);
  }

  onShowNotes() {
    if (!this.assignedLead.is_active) {
      return;
    }
    this.showNotesPopup = true;
  }

  onCloseNotesPopup() {
    this.showNotesPopup = false;
  }
}
